import { composeProgress } from "apollo/utils";
import { GET_AUCTION_FORMS } from "core/apollo/graphql";
import {
  CLINICAL_CONSULTATION_ACTION,
  DOCS_FORM_NAMES,
  SEARCH_ACTION_DELETE_FORM,
} from "core/consts";
import { useApolloEncryptionContext } from "core/model/patients/encryption/utils";
import { getErrorMessage } from "core/model/utils/errors";
import {
  Auction,
  ClinicalConsultation,
  DocsFormName,
  Patient,
  RehabFormName,
} from "core/types";
import { useDeleteUnsharedFileByType } from "dsl/ecosystems/PatientFiles/useDeleteUnsharedFileByType";
import { getRehabFormConfig } from "dsl/ecosystems/PatientForms/RehabForms/configs";
import { useClinicalConsultationAction } from "dsl/hooks";
import useSearchAction from "dsl/hooks/useSearchAction";
import { FORM_NAME_TO_FILE_TYPE } from "./utils";

export function useDeleteConfirmation({
  auction,
  consultation,
  formName,
  patient,
}: {
  auction: Auction | undefined;
  consultation: ClinicalConsultation | undefined;
  formName: DocsFormName;
  patient: Patient;
}) {
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });

  const [deleteAuctionForm, deleteAuctionFormProgress] = useSearchAction({
    actionType: SEARCH_ACTION_DELETE_FORM,
    refetchQueries: [
      {
        context: { encryptionContext },
        query: GET_AUCTION_FORMS,
        variables: {
          auctionId: auction?.id,
        },
      },
    ],
  });

  const [deleteFormConsultation, deleteFormConsultationProgress] =
    useClinicalConsultationAction({
      actionType: CLINICAL_CONSULTATION_ACTION.DELETE_FORM,
    });

  const { formSubType, formType } =
    formName === DOCS_FORM_NAMES.TRANSITIONAL_CARE
      ? { formSubType: undefined, formType: undefined }
      : getRehabFormConfig(formName as RehabFormName) ?? {};

  // todo: remove when all files are synced with forms
  const { deleteUnsharedFile } = useDeleteUnsharedFileByType({
    auctionId: auction?.id ?? 0,
    formSubType,
    formType,
  });

  const onDelete = async () => {
    try {
      if (consultation) {
        await deleteFormConsultation({
          consultation,
          context: {
            form_type: FORM_NAME_TO_FILE_TYPE[formName],
          },
          patient,
        });
        return;
      }
      if (!auction) {
        console.error("auction is not defined");
        return;
      }
      await deleteAuctionForm({
        auction,
        context: {
          form_type: FORM_NAME_TO_FILE_TYPE[formName],
        },
      });

      await deleteUnsharedFile();
    } catch (err) {
      console.error(`error deleting ${formName} form: ${getErrorMessage(err)}`);
    }
  };

  return {
    deleteFormProgress: composeProgress([
      deleteAuctionFormProgress,
      deleteFormConsultationProgress,
    ]),
    onDelete,
  };
}
